import { AbstractControl, FormControl, ValidationErrors, Validators } from '@angular/forms';
import Handsontable from 'handsontable';

import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { DealItemDataConfig } from 'src/app/features/prob/models/deal-item-data-config';
import { DealQuestion } from 'src/app/features/prob/models/deal-question';
import { DealValue } from 'src/app/features/prob/models/deal-value';
import { FormStructureResult } from 'src/app/features/prob/models/form-structure-result';
import { EnumControlTypes, EnumDepOperatorCodes, EnumModelCodes, ValueDependency, ValueOption } from '.';
import { areValuesEquivalent, isgreplaceNthOccurrence } from '../functions';
import { CascadeModelItem } from './cascade-model-Item';
import { EnumDataTypes } from './constants/enumDataTypes';
import { EnumDepActionCodes } from './constants/enumDepActionCodes';
import { signal } from '@angular/core';
import { DealAdjustmentsCache } from './deal-adjustments-cache';

export class FormMetaData {

  // ----------------------- Constants ----------------------------------------

  /** CSS class for visible controls */
  protected readonly ctCssClassShow: string = '';

  /** CSS class for hidden controls */
  protected readonly ctCssClassHide: string = 'isg-nodisplay';

  /** CSS class for enabled controls */
  protected readonly ctCssClassEnable: string = '';

  /** CSS class for disabled controls */
  protected readonly ctCssClassDisable: string = 'isg-disable';

  /** Debounce delay in milliseconds for value change handlers */
  protected readonly ctValueChangeDebounceMs = 100;

  /** Header text type identifier for dependency text replacement */
  protected readonly ctTextTypeHeader = 'Header';

  /** Question text type identifier for dependency text replacement */
  protected readonly ctTextTypeQuestion = 'Question';

  /** Boolean true display text */
  protected readonly ctBoolTrueText = 'Yes';

  /** Boolean false display text */
  protected readonly ctBoolFalseText = 'No';

  /** Scale factor for values displayed in millions */
  protected readonly ctScaleFactorMillions = 1_000_000;

  // -------------------- Signal Properties -----------------------------------

  /** Signal holding current form control values for child component consumption */
  public formCtrlValues = signal<Map<string, string> | null>(null);

  // ------------------- Model Configuration ----------------------------------

  /** Full model configuration supplied for the deal item */
  public modelConfig: DealQuestion[] = [];

  /** Full model configuration indexed by model value identifiers */
  public configMapByModelValueId: Map<string, DealQuestion> = new Map<string, DealQuestion>();

  /** Partial model configuration indexed by variable names */
  public configMapByVName: Map<string, DealQuestion> = new Map<string, DealQuestion>();

  // ------------------- Display Configuration --------------------------------

  /**
   * Partial collection of configs used to generate the visual form UI.
   * Includes structural elements and configs marked to show to the user.
   * Parents set not to show will hide their children.
   */
  public configDisplay: DealQuestion[] = [];

  /** Hierarchical parent-child display configuration */
  public configDisplayHierarchical: DealQuestion[] = [];

  /**
   * Text changes to be applied to headers and questions.
   * Key 1: Header or Question. Key 2: modelValueId. Value: replacement text.
   * Populated when processing dependencies with text replacement.
   */
  public headerTextToBechangedList: Map<string, Map<string, string>> = new Map<string, Map<string, string>>();

  /** CSS classes assigned to controls indexed by varName */
  public cssClassesByVName: { [key: string]: string } = {};

  // ------------------- Data & Form State ------------------------------------

  /** Deal values along with supporting value and model lookup optimization collections */
  public dataConfig: DealItemDataConfig = new DealItemDataConfig();

  /** Form control structure created from processing the model configuration */
  public formStructure: FormStructureResult | null = null;

  /** Current form control values keyed by varName */
  public formControlValues: Map<string, string> = new Map<string, string>();

  // ------------------- Dependency Collections -------------------------------

  /** Controls with dependencies configured, indexed by source varName */
  public dependenciesByVName = new Map<string, ValueDependency[]>();

  // ------------------- Protected Properties ---------------------------------

  /** Grid configs indexed by variable name */
  protected gridConfigsByVName: Map<string, DealQuestion> = new Map<string, DealQuestion>();

  /** Handsontable grid settings indexed by variable name */
  protected hotGridSettingsByVName: Map<string, Handsontable.GridSettings> = new Map<string, Handsontable.GridSettings>();

  /** Tracks which grids have been initialised by variable name / hot ID */
  protected hotIdsInitialized = new Set<string>();

  /**
   * Reverse dependency map for sum calculations.
   * Key: constituent varName (e.g. 'incident-email').
   * Value: set of sum-control varNames (e.g. 'incident-total') to notify on change.
   */
  protected dependencySumMap = new Map<string, Set<string>>();

  /**
   * Sum-control dependencies indexed by the sum-target varName.
   * Key: sum varName (e.g. 'incident-total').
   * Value: dependencies describing constituent controls to sum.
   */
  protected dependencySumByVName = new Map<string, ValueDependency[]>();

  /**
   * Option cascading configuration.
   * Key 1: source varName. Key 2: source value. Key 3: target varName.
   * Value: option string values used to filter the target control.
   */
  protected optionCascadesByVName: Map<string, Map<string, Map<string, string[]>>> = new Map();

  /** Manages subscription cleanup when forms are regenerated */
  protected subsEvents = new Subscription();

  // ----------------------- Dependency Cascade ------------------------------

  /**
   * Returns the varName of the first config whose model code matches.
   * Used by the form component to locate a control by its model code,
   * e.g. when reacting to shared-cache changes.
   */
  public varNameForCodeGet(code: string | null | undefined): string | null {

    if (!code)
      return null;

    const configs = Array.from(this.configMapByVName.values());

    for (let idx = 0; idx < configs.length; idx++) {

      const config = configs[idx];
      if (config.code === code
        && config.varName) {

        return config.varName;
      }
    }

    return null;
  }

  /**
   * Model codes whose value changes should be propagated into the
   * shared DealAdjustmentsCache when this form is the header slide.
   * Add additional codes here as new global adjustments are introduced.
   */
  protected readonly ctWatchedCacheCodes: ReadonlySet<string> = new Set<string>([
    EnumModelCodes.UseCustomAdjustment,
    EnumModelCodes.DealLength
  ]);

  /** Shared adjustments cache. Null when not propagating (e.g. tests, non-header). */
  protected adjustmentsCache: DealAdjustmentsCache | null = null;

  /** True when this form represents the header (Scenario) deal item slide. */
  protected isHeaderSlide: boolean = false;

  // ------------------- Model Config Management ------------------------------

  /** Assigns the model configuration for the deal item */
  public modelConfigSet(configs: DealQuestion[] | null | undefined): void {

    this.modelConfig = configs ?? [];
  }

  // ------------------- Form Structure Management ----------------------------

  /**
   * Returns the form control structure for the deal item.
   * Creates it on first call, returns the cached instance on subsequent calls.
   * Callers must NOT pass these controls into another formGroup
   * — doing so re-parents them and detaches them from the original FormGroup
   */
  public formStructureGet(): FormStructureResult {

    if (this.formStructure != null)
      return this.formStructure;

    let result: FormStructureResult = this.formStructureCreate(this.modelConfig);

    return result;
  }

  /** Resets all internal lookup collections */
  protected formStructureReset(): void {

    this.configDisplay = [];
    this.configDisplayHierarchical = [];
    this.configMapByModelValueId.clear();
    this.configMapByVName.clear();
    this.gridConfigsByVName.clear();
    this.dependencySumByVName.clear();
    this.dependenciesByVName.clear();
    this.dependencySumMap.clear();
    this.optionCascadesByVName.clear();

    this.resetHandlers();
  }

  /** Creates the form structure based on the provided configuration */
  protected formStructureCreate(configs: DealQuestion[] | null | undefined): FormStructureResult {

    let result = new FormStructureResult();
    let formFromConfig: { [key: string]: FormControl; } = result.structure;

    this.formStructureReset();

    if (!configs
      || configs.length === 0) {

      this.dataConfig.configDisplayHierarchical = this.configDisplayHierarchical;
      this.dataConfig.configMapByModelValueId = this.configMapByModelValueId;
      this.formStructure = result;
      return result;
    }

    this.formStructureLookupsFill(configs, result);

    if (result.errorMessage)
      return result;

    this.formStructureCreateControls(formFromConfig, result);

    if (result.errorMessage)
      return result;

    this.formStructureLookupsReverseFill();

    this.formStructureAttachEventHandlers(formFromConfig);

    this.dataConfig.configDisplayHierarchical = this.configDisplayHierarchical;
    this.dataConfig.configMapByModelValueId = this.configMapByModelValueId;

    this.formStructure = result;

    return this.formStructure;
  }

  /**
   * Populates internal collections by processing all configs.
   * Builds lookup maps, hierarchies, dependencies, and determines what to display.
   */
  protected formStructureLookupsFill(configs: DealQuestion[], result: FormStructureResult): void {

    let parentDisplay = new Map<string, boolean | undefined>();
    let parentLookup = new Map<string, DealQuestion>();

    for (let idx = 0; idx < configs.length; idx++) {

      const config = configs[idx];

      if (config.isActive === false)
        continue;

      // Build lookup maps
      this.configMapByModelValueId.set(config.id!, config);

      if (config.varName
        && config.varName.length > 0) {

        this.configMapByVName.set(config.varName, config);
      }

      // Determine visibility with parent overrides
      let show: boolean = config.showToUser ?? false;
      parentLookup.set(config.id!, config);

      if (config.parentId) {

        if (show == true)
          show = parentDisplay.get(config.parentId) ?? false;

      } else {
        parentDisplay.set(config.id!, show);
      }

      // Build parent-child hierarchy
      if (config.parentId
        && config.parentId !== "") {

        let parentConfig: DealQuestion | undefined = parentLookup.get(config.parentId);

        if (parentConfig) {
          parentConfig.hasChildren = true;

          if (!parentConfig.children)
            parentConfig.children = [];

          parentConfig.children.push(config);
        }
      }

      // Collect dependencies
      if (config.dependencies?.length ?? 0 > 0) {

        const srcVarName = config.varName ?? "";
        const dependencies = config.dependencies ?? [];

        if (config.controlCode === EnumControlTypes.SubHeaderSum) {
          this.dependencySumByVName.set(srcVarName, dependencies);
        } else {
          this.dependenciesByVName.set(srcVarName, dependencies);
        }
      }

      // Setup cascading options/picklists
      if (config.varName
        && (config.optionCascadeJson?.length ?? 0 > 0)) {

        const lookup = CascadeModelItem.vNameValueVNameLookupCreate(config.optionCascadeJson ?? '');

        const configMap = lookup.get(config.varName);
        if (configMap)
          this.optionCascadesByVName.set(config.varName, configMap);
      }

      // Add to display collections if visible
      if (!show)
        continue;

      this.configDisplay.push(config);

      if (!config.parentId
        || config.parentId === "") {

        this.configDisplayHierarchical.push(config);
      }

      // Handle grid configs — require varName validation
      const isGridElement = config.controlCode === EnumControlTypes.GridInputByColumn
        || config.controlCode === EnumControlTypes.GridInputByRow
        || config.controlCode === EnumControlTypes.GridWithRows
        || config.controlCode === EnumControlTypes.GridOutput
        || config.controlCode === EnumControlTypes.GridBlr;

      if (isGridElement) {

        if (!config.varName
          || config.varName.length < 1) {

          result.errorMessage = `The grid item: ${config.displayText ?? "Not specified"} [${config.sortOrder}] failed to load as it does not have a variable name specified.`;
          return;
        }

        this.gridConfigsByVName.set(config.varName, config);
      }

      // Handle chart configs — require varName validation
      const isChartElement = config.controlCode === EnumControlTypes.ChartDef;
      if (isChartElement) {

        if (!config.varName
          || config.varName.length < 1) {

          result.errorMessage = `The chart item: ${config.displayText ?? "Not specified"} [${config.sortOrder}] failed to load chart as it does not have a variable name specified.`;
          return;
        }
      }

      // Update parent display for container elements
      const isContainerElement =
        config.controlCode === EnumControlTypes.Header
        || config.controlCode === EnumControlTypes.HeaderClosed
        || config.controlCode === EnumControlTypes.SubHeader
        || config.controlCode === EnumControlTypes.HeaderResults
        || config.controlCode === EnumControlTypes.DisplayBlock;

      if (isContainerElement) {
        parentDisplay.set(config.id!, show);
      }
    }
  }

  /**
   * Processes display configs to create form controls with validators.
   * @param formFromConfig Form control structure to populate.
   * @param result Result object for error messages.
   */
  protected formStructureCreateControls(
    formFromConfig: { [key: string]: FormControl; },
    result: FormStructureResult
  ): void {
    for (let idx = 0; idx < this.configDisplay.length; idx++) {

      const config = this.configDisplay[idx];

      // Skip structural or inactive elements that don't need form controls
      const isStructuralElement = config.controlCode === EnumControlTypes.Header
        || config.controlCode === EnumControlTypes.HeaderClosed
        || config.controlCode === EnumControlTypes.HeaderResults
        || config.controlCode === EnumControlTypes.SubHeader
        || config.controlCode === EnumControlTypes.DisplayBlock
        || config.controlCode === EnumControlTypes.GridInputByColumn
        || config.controlCode === EnumControlTypes.GridInputByRow
        || config.controlCode === EnumControlTypes.GridWithRows
        || config.controlCode === EnumControlTypes.GridOutput
        || config.controlCode === EnumControlTypes.GridBlr
        || config.controlCode === EnumControlTypes.GridCustomAdj
        || config.controlCode === EnumControlTypes.ChartDef
        || config.isMetaData === true
        || config.isActive === false;

      if (isStructuralElement)
        continue;

      // Validate varName exists for form control creation
      if (!config.varName
        || config.varName.length < 1) {

        result.errorMessage = `The item: ${config.displayText ?? "Not specified"} [${config.sortOrder}] failed to load as it does not have a variable name specified.`;
        return;
      }

      // Build validators based on config
      const validators = [];

      if (config.isActive === true) {

        if (config.isRequired === true) {
          validators.push(Validators.required);
          if (config.controlCode === EnumControlTypes.Text && config.code === 'dn')
            validators.push((control: AbstractControl): ValidationErrors | null =>
              typeof control.value === 'string' && control.value.trim().length === 0
                ? { whitespace: true }
                : null
            );
        }

        if (config.min != null && this.isNumericControl(config.controlCode))
          validators.push(Validators.min(config.min));

        if (config.max != null && this.isNumericControl(config.controlCode))
          validators.push(Validators.max(config.max));

        if (config.controlCode === EnumControlTypes.Text && config.code === 'dn' && config.max != null)
          validators.push(Validators.maxLength(config.max));

        if (config.controlCode === EnumControlTypes.Text && config.code === 'dn')
          validators.push((control: AbstractControl): ValidationErrors | null =>
            typeof control.value === 'string' && /[\/\\]/.test(control.value)
              ? { slashNotAllowed: true }
              : null
          );
      }

      // Create form control with appropriate default value
      const value = config.defaultValue ?? null;

      if (value == null
        || value == undefined
        || value === '') {

        formFromConfig[config.varName] = new FormControl(value, validators);

      } else if (config.hasOptions) {

        const option = config.options?.find(x =>
          (x.code && value.localeCompare(x.code, undefined, { sensitivity: 'accent' }) === 0)
          || (x.val && value.localeCompare(x.val, undefined, { sensitivity: 'accent' }) === 0)
          || (x.tagId && value.localeCompare(x.tagId, undefined, { sensitivity: 'accent' }) === 0)
          || (x.id && value.localeCompare(x.id, undefined, { sensitivity: 'accent' }) === 0)
        );

        formFromConfig[config.varName] = new FormControl(option ?? null, validators);

      } else if (config.dataTypeCode === EnumDataTypes.Number) {

        const num = Number(value);

        const scaled = isNaN(num) ? null : this.scaleDefaultForDisplay(config.code, num);
        formFromConfig[config.varName] = new FormControl(scaled, validators);

      } else {
        formFromConfig[config.varName] = new FormControl(value, validators);
      }
    }
  }

  /** True for control types whose value is numeric and can carry min/max bounds. */
  protected isNumericControl(controlCode: string | null | undefined): boolean {
    return controlCode === EnumControlTypes.Double
      || controlCode === EnumControlTypes.WholeNumber
      || controlCode === EnumControlTypes.Percentage
      || controlCode === EnumControlTypes.GridSum
      || controlCode === EnumControlTypes.SubHeaderSum;
  }


  /** Builds reverse lookup maps for sum dependencies */
  protected formStructureLookupsReverseFill(): void {

    const sumEntries = Array.from(this.dependencySumByVName.entries());

    for (let idx = 0; idx < sumEntries.length; idx++) {

      const [srcVarName, dependencies] = sumEntries[idx];

      if (!dependencies
        || dependencies.length === 0) {

        continue;
      }

      for (let i = 0; i < dependencies.length; i++) {

        const dep = dependencies[i];

        if (!this.dependencySumMap.has(dep.tgtVarName!))
          this.dependencySumMap.set(dep.tgtVarName!, new Set());

        this.dependencySumMap.get(dep.tgtVarName!)!.add(srcVarName);
      }
    }
  }

  /**
   * Attaches value change event handlers to form controls.
   * @param formFromConfig Form control structure to attach handlers to.
   */
  /**
   * Attaches value change event handlers to form controls.
   * @param formFromConfig Form control structure to attach handlers to.
   */
  protected formStructureAttachEventHandlers(formFromConfig: { [key: string]: FormControl; }): void {

    const varNamesWithValueChangeHandlers = new Set<string>();

    // Attach handlers for sum calculations
    const sumMapEntries = Array.from(this.dependencySumMap.entries());

    for (let idx = 0; idx < sumMapEntries.length; idx++) {

      const [ctrlVarNameValuePart, srcVarName] = sumMapEntries[idx];
      const control = formFromConfig[ctrlVarNameValuePart];

      if (control
        && !varNamesWithValueChangeHandlers.has(ctrlVarNameValuePart)) {

        const sub = control.valueChanges.pipe(
          distinctUntilChanged(),
          debounceTime(this.ctValueChangeDebounceMs)
        ).subscribe(value => {
          this.onValueDependencyChange(ctrlVarNameValuePart, value);
        });

        this.subsEvents.add(sub);
        varNamesWithValueChangeHandlers.add(ctrlVarNameValuePart);
      }
    }

    // Attach handlers for regular dependencies
    const depEntries = Array.from(this.dependenciesByVName.entries());

    for (let idx = 0; idx < depEntries.length; idx++) {

      const [srcVarName, dependencies] = depEntries[idx];

      if (!dependencies
        || dependencies.length === 0) {

        continue;
      }

      for (let i = 0; i < dependencies.length; i++) {

        const dep: ValueDependency = dependencies[i];

        if ((dep.actionCode === EnumDepActionCodes.Value
          || dep.actionCode === EnumDepActionCodes.Calculation)
          && !varNamesWithValueChangeHandlers.has(srcVarName)) {

          const control = formFromConfig[srcVarName];

          if (control) {

            const sub = control.valueChanges.pipe(
              distinctUntilChanged(),
              debounceTime(this.ctValueChangeDebounceMs)
            ).subscribe(value => {
              this.onValueDependencyChange(srcVarName, value);
            });

            this.subsEvents.add(sub);
            varNamesWithValueChangeHandlers.add(srcVarName);
          }
        }
      }
    }

    // Attach handlers for option cascading
    const cascadeEntries = Array.from(this.optionCascadesByVName.entries());

    for (let idx = 0; idx < cascadeEntries.length; idx++) {

      const [srcVarName, optionCascade] = cascadeEntries[idx];

      if (!varNamesWithValueChangeHandlers.has(srcVarName)) {

        const control = formFromConfig[srcVarName];

        if (control) {

          const sub = control.valueChanges.pipe(
            distinctUntilChanged(),
            debounceTime(this.ctValueChangeDebounceMs)
          ).subscribe(value => {
            this.onValueDependencyChange(srcVarName, value);
          });

          this.subsEvents.add(sub);
          varNamesWithValueChangeHandlers.add(srcVarName);
        }
      }
    }

    // Attach handlers for cache-watched codes that aren't already subscribed
    // via dependencies or cascades. The header slide is the only writer, but
    // we subscribe unconditionally — propagateValueToCache() short-circuits
    // for non-header slides.
    const displayConfigs = this.configDisplay;

    for (let idx = 0; idx < displayConfigs.length; idx++) {

      const config = displayConfigs[idx];
      const code = config.code;
      const varName = config.varName;

      if (!code
        || !varName
        || !this.ctWatchedCacheCodes.has(code)
        || varNamesWithValueChangeHandlers.has(varName)) {

        continue;
      }

      const control = formFromConfig[varName];

      if (control) {

        const sub = control.valueChanges.pipe(
          distinctUntilChanged(),
          debounceTime(this.ctValueChangeDebounceMs)
        ).subscribe(value => {
          this.onValueDependencyChange(varName, value);
        });

        this.subsEvents.add(sub);
        varNamesWithValueChangeHandlers.add(varName);
      }
    }
  }


  /**
   * Wires this form meta to the shared adjustments cache.
   * Only the header slide writes; non-header slides leave isHeader as false
   * and the cache reference is unused for writes.
   * Safe to call with null to detach.
   */
  public adjustmentsCacheSet(cache: DealAdjustmentsCache | null, isHeader: boolean): void {

    this.adjustmentsCache = cache;
    this.isHeaderSlide = isHeader;
  }

  // ------------------- Lifecycle & Reset ------------------------------------

  /**
   * Reevaluates all dependencies and cascades based on current form control values.
   * Call after loading data into the form to ensure correct control state.
   */
  public onLoadRefreshDependencies(): void {

    this.processOnLoadClearState();

    this.processOnLoadDependenciesRegular();

    this.processOnLoadDependenciesHidden();

    this.processOnLoadDependencySums();

    this.processOnLoadCascades();
  }

  /** Clears all event handler subscriptions enrolled for the form controls */
  public resetHandlers(): void {

    this.subsEvents.unsubscribe();
    this.subsEvents = new Subscription();
  }

  /**
   * Re-runs all dependencies registered for a single varName.
   * Used when an external source (e.g. shared adjustments cache) updates
   * a value outside the normal valueChanges flow and dependent UI state
   * needs to be re-evaluated.
   *
   * The varName may resolve to either:
   *   - A visible form control (value read from formStructure)
   *   - A hidden data-only entry (value read from dataConfig lookup)
   */
  public refreshDependenciesForVarName(varName: string | null | undefined): void {

    if (!varName)
      return;

    let valueText = '';

    const control = this.formStructure?.structure[varName];

    if (control) {

      valueText = this.extractValueAsText(control.value);

    } else {

      const dealValues = this.dataConfig.dataMapByVarNameCode?.get(varName);

      if (dealValues
        && dealValues.length > 0) {

        valueText = this.extractDealValueAsText(dealValues[0]);
      }
    }

    this.processDependencies(varName, valueText, false);
  }

  /**
 * Applies a cache-driven value for a single watched model code.
 * Routes to either the visible form control (setValue) or the hidden
 * data-only path (DealItemState.applyCacheValueByCode + dependency refresh).
 *
 * Returns true when the underlying value actually changed — callers use
 * this to decide whether to mark the slide dirty.
 *
 * @param code         The model code to apply (must be in ctWatchedCacheCodes)
 * @param value        New value from the cache
 * @param stateApply   Callback to apply hidden-path value into the state.
 *                     Receives (code, value, dataTypeCode), returns true on change.
 *                     Typically wired to DealItemState.applyCacheValueByCode.
 */
  public applyCacheValueForCode(
    code: string | null | undefined,
    value: any,
    stateApply: (code: string, value: any, dataTypeCode: string | null | undefined) => boolean
  ): boolean {

    if (!code
      || !this.ctWatchedCacheCodes.has(code)) {

      return false;
    }

    const varName = this.varNameForCodeGet(code);
    if (!varName)
      return false;

    const control = this.formStructure?.structure[varName];

    if (control) {

      // Visible-switch path. setValue with emitEvent: true so any local
      // dependencies on this control re-evaluate, and the form's own change
      // tracking marks the slide dirty.
      if (control.value === value)
        return false;

      control.setValue(value);
      return true;
    }

    // Hidden path — defer to the state to mutate the DealValue and
    // record it in dealValueChanges, then re-run dependencies for varName.
    const config = this.configMapByVName.get(varName);
    if (!config)
      return false;

    const wasChanged = stateApply(code, value, config.dataTypeCode);

    if (!wasChanged)
      return false;

    this.refreshDependenciesForVarName(varName);

    return true;
  }

  /**
   * First-load catch-up: applies all watched cache values to this form,
   * after the server data has been loaded and initial dependencies have run.
   *
   * Used because the runtime effect in the form component runs during slide
   * creation — before configs and data have landed — so the very first cache
   * read finds nothing to apply. This method runs once, after addFormData,
   * to overlay any cache values that diverge from server-loaded values.
   *
   * Returns true when at least one watched value changed (caller marks dirty).
   *
   * @param cache       The shared adjustments cache (caller passes the input value)
   * @param stateApply  Hidden-path apply callback (see applyCacheValueForCode)
   */
  public applyCacheValuesOnLoad(
    cache: DealAdjustmentsCache | null,
    stateApply: (code: string, value: any, dataTypeCode: string | null | undefined) => boolean
  ): boolean {

    if (!cache)
      return false;

    let anyChanged = false;

    const watchedCodes = Array.from(this.ctWatchedCacheCodes);

    for (let idx = 0; idx < watchedCodes.length; idx++) {

      const code = watchedCodes[idx];
      const cacheValue = this.readCacheValueForCode(cache, code);

      // null means "not set globally" — nothing to overlay.
      if (cacheValue === null
        || cacheValue === undefined) {

        continue;
      }

      const changed = this.applyCacheValueForCode(code, cacheValue, stateApply);
      if (changed)
        anyChanged = true;
    }

    return anyChanged;
  }

  /**
   * Reads the current cache signal value for a given watched code.
   * Centralises the code-to-signal mapping so callers don't need to know it.
   */
  protected readCacheValueForCode(cache: DealAdjustmentsCache, code: string): any {

    switch (code) {

      case EnumModelCodes.UseCustomAdjustment:
        return cache.customAdjustmentGlobal();

      case EnumModelCodes.DealLength:
        return cache.durationGlobal();

      // Future watched codes wire their signal reads in here.

      default:
        return null;
    }
  }

  // ------------------- Deal Value Lookups -----------------------------------

  /**
   * Refreshes deal value lookup stores for the deal item.
   * Injected values are populated first, then deal values.
   * Duplicates can exist as they are appended to the list of matches.
   */
  public dealValueRefreshLookups(
    data: DealValue[] | null | undefined,
    injectedValues: DealValue[] | null | undefined
  ): void {

    let dealValuesMapByModelValueId = new Map<string, DealValue[]>();
    let dealValuesMapByVarNameCode = new Map<string, DealValue[]>();

    let dataConfig = this.dataConfig;

    dataConfig.dataMapByModelValueId = dealValuesMapByModelValueId;
    dataConfig.dataMapByVarNameCode = dealValuesMapByVarNameCode;

    if (injectedValues
      && injectedValues.length > 0) {

      for (let idx = 0; idx < injectedValues.length; idx++) {

        const value = injectedValues[idx];

        const keyVName = value.varName;
        if (keyVName
          && keyVName.length > 0) {

          const existingVnValues = dealValuesMapByVarNameCode.get(keyVName) || [];
          existingVnValues.push(value);

          if (!dealValuesMapByVarNameCode.has(keyVName))
            dealValuesMapByVarNameCode.set(keyVName, existingVnValues);
        }

        const keyCode = value.code;
        if (keyCode
          && keyCode.length > 0) {

          const existingCodeValues = dealValuesMapByVarNameCode.get(keyCode) || [];
          existingCodeValues.push(value);

          if (!dealValuesMapByVarNameCode.has(keyCode))
            dealValuesMapByVarNameCode.set(keyCode, existingCodeValues);
        }
      }
    }

    if (!data)
      return;

    for (let idx = 0; idx < data.length; idx++) {

      const value = data[idx];

      const keyMv = value.modelValueId!;

      const existingValues = dealValuesMapByModelValueId.get(keyMv) || [];
      existingValues.push(value);

      if (!dealValuesMapByModelValueId.has(keyMv))
        dealValuesMapByModelValueId.set(keyMv, existingValues);

      const keyVName = value.varName;
      if (keyVName
        && keyVName.length > 0) {

        const existingVnValues = dealValuesMapByVarNameCode.get(keyVName) || [];
        existingVnValues.push(value);

        if (!dealValuesMapByVarNameCode.has(keyVName))
          dealValuesMapByVarNameCode.set(keyVName, existingVnValues);
      }

      const keyCode = value.code;
      if (keyCode
        && keyCode.length > 0) {

        const existingCodeValues = dealValuesMapByVarNameCode.get(keyCode) || [];
        existingCodeValues.push(value);

        if (!dealValuesMapByVarNameCode.has(keyCode))
          dealValuesMapByVarNameCode.set(keyCode, existingCodeValues);
      }
    }

    this.sortDealValueMapByRowNo(dealValuesMapByModelValueId);

    this.sortDealValueMapByRowNo(dealValuesMapByVarNameCode);
  }

  // ------------------- Dependency Text Replacement --------------------------

  /**
   * Replaces text in headers or questions based on dependency rules and updates control values.
   * @param matchOnValue The value to match and use for replacement.
   * @param dep The dependency configuration containing replacement rules.
   * @param targetModelValueId The model value identifier of the target control.
   * @param isOnLoad Whether this is being called during form initialization.
   */
  public processDependenciesTextReplace(matchOnValue: any, dep: ValueDependency, targetModelValueId: string, isOnLoad: boolean): void {

    let tmpTextToBeChangedList: any = new Map<string, string>();

    let config = this.dataConfig.configMapByModelValueId?.get(targetModelValueId);

    if (!config)
      return;

    switch (dep.operatorCode) {

      case EnumDepOperatorCodes.TxtHeader: {

        tmpTextToBeChangedList = this.controlApplyTextChange(tmpTextToBeChangedList, this.ctTextTypeHeader, config, dep, matchOnValue);

        break;
      }

      case EnumDepOperatorCodes.TxtQuestion: {

        tmpTextToBeChangedList = this.controlApplyTextChange(tmpTextToBeChangedList, this.ctTextTypeQuestion, config, dep, matchOnValue);

        break;
      }

      case EnumDepOperatorCodes.SetValue:

        if (dep.tgtVarName) {
          let ctrl = this.formStructure?.structure[dep.tgtVarName];
          if (ctrl)
            ctrl.setValue(!isNaN(Number(matchOnValue)) ? Number(matchOnValue) : matchOnValue);
        }
        break;

      default:
        break;
    }
  }

  // ------------------- Handsontable Grid ------------------------------------

  /** Returns Handsontable grid settings for the given deal question */
  public hotGridGetSettings(config: DealQuestion): Handsontable.GridSettings {

    console.log('getHotGridSettings', config.varName);

    if (!config.varName
      || config.varName.length < 1)
      return {};

    const key: string = config.varName;

    if (!this.hotGridSettingsByVName.has(key)) {

      let settings: Handsontable.GridSettings = {
        colHeaders: this.hotHeaderNamesGet(config)
      };

      this.hotGridSettingsByVName.set(key, settings);
    }

    return this.hotGridSettingsByVName.get(key)!;
  }

  /** Clears the initialised grid tracking */
  public hotGridClear(): void {

    this.hotIdsInitialized.clear();
  }

  /** Creates header name configurations for the Handsontable grid including styling */
  protected hotHeaderNamesGet(config?: DealQuestion): string[] {

    if (!config
      || config.hasChildren === false
      || !config.children
      || (config.children?.length ?? 0) < 1) {

      return [];
    }

    let result: string[] = [];

    for (let pos = 0; pos < config.children.length; pos++) {

      let item: DealQuestion = config.children[pos];

      if (item.showToUser == true
        && item.controlCode != EnumControlTypes.GridRows)
        result.push(item.displayText ?? "");
    }

    return result;
  }

  // ------------------- Event Processing -------------------------------------

  /**
   * Responds to a value change event for a control with dependencies.
   * Handles sum calculations and triggers the dependency processing chain.
   */
  protected onValueDependencyChange(changedVarName: string, newObjVal: any): void {

    const valueChangedUpdateSum = this.dependencySumMap.get(changedVarName);

    const newValueText = this.extractValueAsText(newObjVal);

    if (valueChangedUpdateSum
      && valueChangedUpdateSum.size > 0) {

      const sumVarNames = Array.from(valueChangedUpdateSum);

      for (let idx = 0; idx < sumVarNames.length; idx++) {

        const varName = sumVarNames[idx];
        const dependencies = this.dependencySumByVName.get(varName) ?? [];
        this.calculateAndSetSum(varName, dependencies);
      }
    }

    this.processDependencies(changedVarName, newValueText, false);

    this.propagateValueToCache(changedVarName, newObjVal);
  }

  // ----------------------- Dependency Cascade ------------------------------

  /**
   * Propagates a value change for a watched control into the shared cache.
   * No-op unless this is the header slide and the changed control's code
   * is in the watch list.
   */
  protected propagateValueToCache(changedVarName: string, newValue: any): void {

    if (!this.isHeaderSlide
      || !this.adjustmentsCache) {

      return;
    }

    const config = this.configMapByVName.get(changedVarName);
    const code = config?.code;

    if (!code
      || !this.ctWatchedCacheCodes.has(code)) {

      return;
    }

    switch (code) {

      case EnumModelCodes.UseCustomAdjustment: {

        const boolVal = this.coerceToNullableBoolean(newValue);
        this.adjustmentsCache.setCustomAdjustmentGlobal(boolVal);
        break;
      }

      case EnumModelCodes.DealLength: {

        const numVal = this.coerceToNumberOrNull(newValue);
        this.adjustmentsCache.setDurationGlobal(numVal);
        break;
      }

      // Future watched codes wire in here.

      default:
        break;
    }
  }

  /** Coerces the various YesNo / boolean / numeric representations into boolean | null */
  protected coerceToNullableBoolean(val: any): boolean | null {

    if (val == null
      || val === '')
      return null;

    if (typeof val === 'boolean')
      return val;

    if (typeof val === 'number')
      return val !== 0;

    if (typeof val === 'string') {

      const lowered = val.trim().toLowerCase();
      if (lowered === 'true' || lowered === 'yes' || lowered === '1')
        return true;
      if (lowered === 'false' || lowered === 'no' || lowered === '0')
        return false;
    }

    return null;
  }

  /** Coerces booleans / strings / numbers into a number or null */
  protected coerceToNumberOrNull(val: any): number | null {

    if (val == null
      || val === '')
      return null;

    if (typeof val === 'boolean')
      return val ? 1 : 0;

    if (typeof val === 'number')
      return isNaN(val) ? null : val;

    const num = Number(val);
    return isNaN(num) ? null : num;
  }

  // ------------------- OnLoad Dependency Processing -------------------------

  /**
 * Resets visual-state maps at the start of each dependency refresh pass.
 *
 * Required because controlApplyVisualState only writes on rule match — there
 * is no else branch to clear entries when a rule's condition flips from true
 * to false. Without this reset, hide/show classes from previous evaluations
 * persist and accumulate across refreshes.
 */
  protected processOnLoadClearState() {
    this.cssClassesByVName = {};
    this.headerTextToBechangedList.clear();
  }

  /**
   * Processes regular dependencies to set initial visibility, enabled state, and text.
   * Includes controls with null/undefined values so "Does not equal" dependencies work correctly.
   */
  protected processOnLoadDependenciesRegular(): void {

    if (!this.dependenciesByVName
      || this.dependenciesByVName.size === 0) {

      return;
    }

    const entries = Array.from(this.dependenciesByVName.entries());

    for (let idx = 0; idx < entries.length; idx++) {

      const [srcVarName, dependencies] = entries[idx];

      if (!dependencies
        || dependencies.length === 0) {

        continue;
      }

      const control = this.formStructure?.structure[srcVarName];

      // Skip only if control doesn't exist — not if value is null/undefined
      if (!control)
        continue;

      const valueText = this.extractValueAsText(control.value);

      this.processDependencies(srcVarName, valueText, true);
    }
  }

  /**
   * Processes dependencies for hidden controls (showToUser: false) that have API values.
   * These controls don't have form controls, so values come from dataConfig.
   */
  protected processOnLoadDependenciesHidden(): void {

    if (!this.dependenciesByVName
      || this.dependenciesByVName.size === 0) {

      return;
    }

    const entries = Array.from(this.dependenciesByVName.entries());

    for (let idx = 0; idx < entries.length; idx++) {

      const [srcVarName, dependencies] = entries[idx];

      if (!dependencies
        || dependencies.length === 0) {

        continue;
      }

      // Skip controls with form controls — already handled by processOnLoadDependenciesRegular
      const control = this.formStructure?.structure[srcVarName];
      if (control)
        continue;

      const dealValues = this.dataConfig.dataMapByVarNameCode?.get(srcVarName);
      if (!dealValues
        || dealValues.length === 0) {

        continue;
      }

      const dealValue = dealValues[0];

      const valueText = this.extractDealValueAsText(dealValue);

      this.processDependencies(srcVarName, valueText, true);
    }
  }

  /** Processes sum dependencies to calculate initial totals */
  protected processOnLoadDependencySums(): void {

    if (!this.dependencySumByVName
      || this.dependencySumByVName.size === 0) {

      return;
    }

    const entries = Array.from(this.dependencySumByVName.entries());

    for (let idx = 0; idx < entries.length; idx++) {

      const [sumVarName, dependencies] = entries[idx];

      if (!dependencies
        || dependencies.length === 0) {

        continue;
      }

      this.calculateAndSetSum(sumVarName, dependencies);
    }
  }

  /**
   * Processes option cascades to filter dependent dropdowns and auto-select values.
   * Handles preservation of existing values and auto-selection when only one option available.
   */
  protected processOnLoadCascades(): void {

    if (!this.optionCascadesByVName
      || this.optionCascadesByVName.size === 0) {

      return;
    }

    const entries = Array.from(this.optionCascadesByVName.entries());

    for (let idx = 0; idx < entries.length; idx++) {

      const [srcVarName, cascadeMap] = entries[idx];

      const srcControl = this.formStructure?.structure[srcVarName];
      if (!srcControl
        || !srcControl.value) {

        continue;
      }

      const srcValueText = this.extractValueAsText(srcControl.value);

      if (!srcValueText)
        continue;

      this.processOptionCascadeWithPreservation(srcVarName, srcValueText);
    }
  }

  // ------------------- Dependency Processing --------------------------------

  /**
   * Processes all dependencies for a changed control value.
   * @param changedVarName The variable name of the control that changed.
   * @param newValueText The new value as text.
   * @param isOnLoad Whether this is being called during form initialization.
   */
  protected processDependencies(changedVarName: string, newValueText: string, isOnLoad: boolean): void {

    const dependencies = this.dependenciesByVName.get(changedVarName) ?? [];

    if (dependencies.length > 0) {

      for (let i = 0; i < dependencies.length; i++) {

        const dep: ValueDependency = dependencies[i];
        this.processValueDependencies(dep.tgtVarName, newValueText, dep, isOnLoad);
      }
    }

    this.processOptionCascade(changedVarName, newValueText, isOnLoad);
  }

  /**
   * Processes value dependencies for a specific target control based on operator rules.
   * @param tgtVarName The target control variable name.
   * @param matchOnValue The value to match against dependency rules.
   * @param dep The dependency configuration.
   * @param isOnLoad Whether this is being called during form initialization.
   */
  protected processValueDependencies(tgtVarName: string | null | undefined, matchOnValue: any, dep: ValueDependency, isOnLoad: boolean): void {

    let resolvedTgtVarName = tgtVarName;

    if (!resolvedTgtVarName
      && dep.tgtModelValueId) {

      const config = this.configMapByModelValueId.get(dep.tgtModelValueId);
      resolvedTgtVarName = config?.varName ?? null;
    }

    if (!resolvedTgtVarName)
      return;

    let ctrl = this.formStructure?.structure[resolvedTgtVarName];
    if (!dep)
      return;

    const tgtControl = this.formStructure?.structure[resolvedTgtVarName];

    if (!tgtControl) {

      const config = this.configMapByVName.get(resolvedTgtVarName);
      if (!config)
        return;

      if (config.children
        && config.children.length > 0) {

        for (let idx = 0; idx < config.children.length; idx++) {

          const child = config.children[idx];
          if (child.varName) {
            this.processValueDependencies(child.varName, matchOnValue, dep, isOnLoad);
          }
        }
      }
    }

    switch (dep.operatorCode) {

      case EnumDepOperatorCodes.Equal: {

        if (areValuesEquivalent(matchOnValue, dep.srcValueMatchOn ?? ''))
          this.controlApplyVisualState(dep, resolvedTgtVarName, ctrl, isOnLoad);

        break;
      }

      case EnumDepOperatorCodes.NotEqual: {

        if (!areValuesEquivalent(matchOnValue, dep.srcValueMatchOn ?? ''))
          this.controlApplyVisualState(dep, resolvedTgtVarName, ctrl, isOnLoad);

        break;
      }

      case EnumDepOperatorCodes.TxtHeader: {

        if (dep.tgtModelValueId)
          this.processDependenciesTextReplace(matchOnValue, dep, dep.tgtModelValueId, false);
        break;
      }

      case EnumDepOperatorCodes.Formula: {

        const config = this.configMapByVName.get(resolvedTgtVarName);
        if (!config)
          return;

        this.processFormula(config);

        break;
      }

      default:
        break;
    }
  }

  /** Processes the formula when any dependent control has been changed */
  protected processFormula(formulaQuestion: DealQuestion): void {

    const formula = formulaQuestion.formula;

    if (formula) {

      const matchedVarnames = [...formula.matchAll(/\[(.*?)\]/g)].map(m => m[1]);
      const uniqueVarnames = Array.from(new Set(matchedVarnames));

      for (let i = 0; i < uniqueVarnames.length; i++) {

        let ctrl = this.formStructure?.structure[uniqueVarnames[i]];
        if (ctrl) {
          this.formControlValues.set(uniqueVarnames[i], this.extractValueAsText(ctrl.value));
        }
      }

      // Trigger signal to pass to child components
      this.formCtrlValues.set(this.formControlValues);
      this.formCtrlValues.update(map => {
        const newMap = new Map(map);
        return newMap;
      });
    }
  }

  // ------------------- Option Cascade Processing ----------------------------

  /**
   * Processes option cascading to filter available options in dependent controls.
   * Auto-selects only when one option is available during value changes.
   */
  protected processOptionCascade(srcVarName: string, newValueText: string, isOnLoad: boolean): void {

    if (!this.optionCascadesByVName.has(srcVarName))
      return;

    const destVarName: string = CascadeModelItem.vNameValueVNameGetLastKey(this.optionCascadesByVName, srcVarName, newValueText) ?? '';

    const controlDest: FormControl<any> | undefined = this.formStructure?.structure[destVarName];

    if (controlDest) {

      const arrValues = CascadeModelItem.vNameValueVNameGetValues(this.optionCascadesByVName, srcVarName, newValueText, destVarName);

      const dealQu: DealQuestion | undefined = this.configMapByVName.get(destVarName);

      if (dealQu) {

        dealQu.filteredOptions = ValueOption.filterOptionsByValues(dealQu.options ?? [], arrValues);

        // Auto-select first item only on dropdown change, not on load
        if (dealQu.filteredOptions
          && dealQu.filteredOptions.length > 0
          && !isOnLoad) {

          let sel: ValueOption = dealQu.filteredOptions[0];
          controlDest.setValue(sel);
        }
      }
    }
  }

  /**
   * Processes option cascading with preservation of existing values.
   * Extended version used during onLoad — preserves existing values even if not in the filtered list.
   */
  protected processOptionCascadeWithPreservation(srcVarName: string, srcValueText: string): void {

    const destVarName: string = CascadeModelItem.vNameValueVNameGetLastKey(
      this.optionCascadesByVName,
      srcVarName,
      srcValueText
    ) ?? '';

    if (!destVarName)
      return;

    const controlDest = this.formStructure?.structure[destVarName];
    const dealQu = this.configMapByVName.get(destVarName);

    if (!controlDest
      || !dealQu) {

      return;
    }

    const arrValues = CascadeModelItem.vNameValueVNameGetValues(
      this.optionCascadesByVName,
      srcVarName,
      srcValueText,
      destVarName
    );

    dealQu.filteredOptions = ValueOption.filterOptionsByValues(dealQu.options ?? [], arrValues);

    const currentDestValue = controlDest.value;

    const currentValueInFilteredOptions = dealQu.filteredOptions?.some(opt =>
      this.cascadeValueMatches(opt, currentDestValue)
    );

    if (currentDestValue
      && currentValueInFilteredOptions) {

      // Current value exists in filtered options — keep it

    } else if (currentDestValue
      && !currentValueInFilteredOptions) {

      // Current value exists but NOT in filtered options — preserve it anyway
      if (dealQu.filteredOptions
        && !dealQu.filteredOptions.some(opt =>
          this.cascadeValueMatches(opt, currentDestValue))) {

        if (typeof currentDestValue === 'object'
          && currentDestValue !== null) {

          dealQu.filteredOptions.unshift(currentDestValue as ValueOption);
        }
      }

    } else if ((!currentDestValue
      || currentDestValue === null
      || currentDestValue === undefined)
      && dealQu.filteredOptions
      && dealQu.filteredOptions.length === 1) {

      // No current value and only one filtered option — auto-select it
      controlDest.setValue(dealQu.filteredOptions[0], { emitEvent: false });
    }
    // else: No current value and multiple/zero filtered options — leave empty
  }

  // ------------------- Control Visual State ---------------------------------

  /**
   * Applies visual changes to the control including CSS class changes
   * for hidden/show and enabling/disabling controls.
   */
  protected controlApplyVisualState(dep: ValueDependency, tgtVarName: string, ctrl: FormControl<any> | undefined, isOnLoad: boolean): void {

    if (dep.tgtIsHidden === true) {

      let cssClasses = this.cssClassesByVName[tgtVarName] ?? '';
      this.cssClassesByVName[tgtVarName] = this.cssClassUpdate(cssClasses, this.ctCssClassHide, this.ctCssClassShow);
    }

    if (dep.tgtShow === true) {

      let cssClasses = this.cssClassesByVName[tgtVarName] ?? '';
      this.cssClassesByVName[tgtVarName] = this.cssClassUpdate(cssClasses, this.ctCssClassShow, this.ctCssClassHide);
    }

    if (dep.tgtIsEnabled === true)
      ctrl?.enable();

    if (dep.tgtIsDisabled === true)
      ctrl?.disable();

    if (dep.tgtReset === true
      && !isOnLoad) {

      ctrl?.setValue(!isNaN(Number(dep.tgtResetValue)) ? Number(dep.tgtResetValue) : dep.tgtResetValue);
    }
  }

  /**
   * Applies text replacement operations to control display text based on dependency rules.
   * @param tmpTextToBeChangedList Temporary list of text changes to apply.
   * @param textType Type of text being changed (Header or Question).
   * @param config The deal question configuration.
   * @param dep The dependency configuration containing replacement rules.
   * @param matchOnValue The value to use in the replacement.
   */
  protected controlApplyTextChange(tmpTextToBeChangedList: any, textType: string, config: DealQuestion, dep: ValueDependency, matchOnValue: any): any {

    if (this.headerTextToBechangedList.has(textType)) {
      tmpTextToBeChangedList = this.headerTextToBechangedList.get(textType);
    }

    let displayText = config.displayText;

    if (dep?.tgtModelValueId
      && displayText) {

      if (dep?.txtReplaceAll) {
        tmpTextToBeChangedList.set(dep?.tgtModelValueId, matchOnValue);
      }

      if (dep?.txtReplacePattern) {

        if (Number(dep?.txtReplaceGroup) > 0) {
          tmpTextToBeChangedList.set(dep?.tgtModelValueId, isgreplaceNthOccurrence(displayText, dep?.txtReplacePattern,
            matchOnValue, Number(dep?.txtReplaceGroup)));
        } else {
          tmpTextToBeChangedList.set(dep?.tgtModelValueId, displayText.replace(dep?.txtReplacePattern, matchOnValue));
        }
      }

      if (dep?.txtSplitDelimiter) {

        if (Number(dep?.txtReplaceGroup) > 0) {

          let splittedList = displayText.split(dep?.txtSplitDelimiter);

          if (splittedList.length > 0
            && Number(dep?.txtReplaceGroup) < splittedList.length) {

            splittedList[Number(dep?.txtReplaceGroup)] = " " + matchOnValue;
            let splittedAndCombined = splittedList.join(dep?.txtSplitDelimiter);
            tmpTextToBeChangedList.set(dep?.tgtModelValueId, splittedAndCombined);
          }
        }
      }
    }

    this.headerTextToBechangedList.set(textType, tmpTextToBeChangedList);
    return tmpTextToBeChangedList;
  }

  // ------------------- Data Transformation ----------------------------------

  /**
   * Extracts a control value as a text string, handling various value types safely.
   * @param value The value to extract (string, number, boolean, object, etc).
   */
  protected extractValueAsText(value: any): string {

    if (value == null
      || value == undefined) {

      return '';
    }

    if (typeof value === 'string')
      return value;

    if (typeof value === 'number')
      return value.toString();

    if (typeof value === 'boolean')
      return value ? this.ctBoolTrueText : this.ctBoolFalseText;

    if (typeof value === 'object') {
      return value['val'] ?? value['value'] ?? value['code'] ?? '';
    }

    return '';
  }

  /**
   * Extracts a value from a DealValue object as text for dependency matching.
   * Handles different data types and control types.
   */
  protected extractDealValueAsText(dealValue: DealValue): string {

    if (!dealValue)
      return '';

    if (dealValue.nVal != null
      && dealValue.nVal != undefined)
      return dealValue.nVal.toString();

    if (dealValue.sVal != null
      && dealValue.sVal != undefined)
      return dealValue.sVal;

    if (dealValue.options
      && dealValue.options.length > 0) {

      const firstOption = dealValue.options[0];
      return firstOption.val ?? '';
    }

    if (dealValue.dtVal != null
      && dealValue.dtVal != undefined)
      return dealValue.dtVal.toString();

    if (dealValue.gVal != null
      && dealValue.gVal != undefined)
      return dealValue.gVal;

    return '';
  }

  /**
   * Calculates a sum from dependencies and updates the sum control.
   * @param sumVarName The variable name of the sum control.
   * @param dependencies The dependencies to sum.
   */
  protected calculateAndSetSum(sumVarName: string, dependencies: ValueDependency[]): void {

    const sum = dependencies.reduce((acc, dependency) => {
      const val = this.formStructure?.structure[dependency.tgtVarName!]?.value;
      return acc + (typeof val === 'number' ? val : 0);
    }, 0);

    const ctrl = this.formStructure?.structure[sumVarName];
    if (ctrl) {

      ctrl.setValue(sum, { emitEvent: false });

      const confDet = this.configMapByVName.get(sumVarName);
      if (confDet?.controlCode == EnumControlTypes.SubHeaderSum) {

        if (confDet?.min != null
          && confDet?.max != null) {

          if (sum < confDet?.min
            || sum > confDet?.max) {

            ctrl.markAsDirty();
            ctrl.updateValueAndValidity();
          }
        }
      }
    }
  }

  /**
   * Checks if a value option matches a control value.
   * @param option The option to check.
   * @param value The control value to match against.
   */
  protected cascadeValueMatches(option: ValueOption, value: any): boolean {

    if (!value)
      return false;

    if (typeof value === 'string') {
      return option.val === value
        || option.code === value
        || option.id === value
        || option.tagId === value;
    }

    if (typeof value === 'object') {

      const valStr = value['val'] ?? value['value'] ?? value['code'] ?? value['id'];
      return option.val === valStr
        || option.code === valStr
        || option.id === valStr
        || option.tagId === valStr;
    }

    return false;
  }

  /** Sorts a deal value map by rowNo in ascending order */
  protected sortDealValueMapByRowNo(mapToSort: Map<string, DealValue[]>): void {

    const entries = Array.from(mapToSort.entries());

    for (let idx = 0; idx < entries.length; idx++) {

      const [, values] = entries[idx];

      values.sort((a, b) => {
        const rowNoA = a.rowNo ?? Number.MAX_SAFE_INTEGER;
        const rowNoB = b.rowNo ?? Number.MAX_SAFE_INTEGER;
        return rowNoA - rowNoB;
      });
    }
  }

  // -------------------------Value scaling  ---------------------------------

  /**
 * Returns the model-config default value rescaled into form-space for any
 * model code flagged as displayed in millions. Returns the value unchanged
 * for any other code.
 */
  protected scaleDefaultForDisplay(code: string | null | undefined, val: number): number {

    if (code === EnumModelCodes.ExpectedSpend)
      return val / this.ctScaleFactorMillions;

    return val;
  }

  // ------------------- DOM Class Management ---------------------------------

  /**
   * Returns a DOM element for a control by its varName.
   * Searches by ID, data attribute, then class name.
   */
  protected controlElementGet(varName: string): HTMLElement | null {

    if (!varName
      || varName.length < 1) {

      return null;
    }

    let element = document.getElementById(varName) as HTMLElement;

    if (!element) {
      element = document.querySelector(`[data-varname="${varName}"]`) as HTMLElement;
    }

    if (!element) {
      element = document.querySelector(`.${varName}`) as HTMLElement;
    }

    return element;
  }

  /**
   * Updates CSS classes on a DOM element by adding and removing specified classes.
   * @param element The HTML element to update.
   * @param classToAdd The CSS class to add.
   * @param classToRemove The CSS class to remove.
   */
  protected controlElementCssClassesUpdate(element: HTMLElement, classToAdd: string, classToRemove: string): void {

    if (!element
      || !element.classList) {

      return;
    }

    if (classToRemove
      && classToRemove.length > 0
      && element.classList.contains(classToRemove)) {

      element.classList.remove(classToRemove);
    }

    if (classToAdd
      && classToAdd.length > 0
      && !element.classList.contains(classToAdd)) {

      element.classList.add(classToAdd);
    }
  }

  /**
   * Manipulates an existing CSS class string to add or remove classes.
   * @param cssClasses The current CSS class string.
   * @param classToAdd The class to add.
   * @param classToRemove The class to remove.
   */
  protected cssClassUpdate(cssClasses: string, classToAdd: string, classToRemove: string): string {

    let css = cssClasses || '';

    if (classToRemove
      && classToRemove.length > 0
      && css.includes(classToRemove)) {

      css = css.replace(classToRemove, '').trim();
    }

    if (classToAdd
      && classToAdd.length > 0
      && !css.includes(classToAdd)) {

      css += ` ${classToAdd}`.trim();
    }

    return css;
  }
}

